<template>
  <div class="cultivation-room">
    <div class="room-header">
      <div class="header-info">
        <div class="room-icon" style="background: linear-gradient(135deg, #19be6b 0%, #47c9af 100%)">
          <Icon type="ios-body" size="24" color="#fff" />
        </div>
        <div>
          <h3 class="room-title">修炼室</h3>
          <p class="room-desc">运转周天，凝聚灵气</p>
        </div>
      </div>
      <div class="header-stats">
        <div class="stat-item">
          <span class="stat-label">已分配</span>
          <span class="stat-value">{{ points }}时辰</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">每时辰产出</span>
          <span class="stat-value">{{ outputPerHour }}灵气</span>
        </div>
      </div>
      <Button type="info" size="small" @click="$emit('upgrade')">
        <Icon type="ios-arrow-up" /> Lv.{{ level }}
      </Button>
    </div>

    <div class="room-content">
      <Row :gutter="20">
        <Col :xs="24" :sm="12">
          <div class="spirit-card">
            <div class="card-header">
              <Icon type="ios-flash" color="#ff9900" />
              <span>灵气凝聚</span>
            </div>
            <div class="spirit-display">
              <div class="spirit-circle">
                <div class="spirit-amount">{{ Math.floor(spiritValue) }}</div>
                <div class="spirit-unit">点灵气</div>
              </div>
              <div class="spirit-rate">
                <Icon type="ios-time" />
                每小时 = 基础产出 × (1 + 时辰加成)
              </div>
            </div>
            <div class="collect-section">
              <InputNumber
                v-model="collectAmount"
                :min="1"
                :max="Math.max(1, Math.floor(spiritValue))"
                :step="1"
                placeholder="收取数量"
                style="width: 100%"
              />
              <Button
                type="success"
                long
                size="large"
                style="margin-top: 10px"
                :disabled="spiritValue <= 0 || collectAmount <= 0"
                :loading="collecting"
                @click="handleCollect"
              >
                收取灵气
              </Button>
            </div>
          </div>
        </Col>

        <Col :xs="24" :sm="12">
          <div class="breach-card">
            <div class="card-header">
              <Icon type="ios-school" color="#2db7f5" />
              <span>境界突破</span>
            </div>
            <div class="breach-content" v-if="breachInfo">
              <div class="breach-requirements">
                <div class="require-title">突破至 {{ breachInfo.realmName }} 需要:</div>
                <div class="require-list">
                  <div class="require-item">
                    <span>当前修为</span>
                    <span class="progress">{{ displayExp }}/{{ displayMaxExp }}</span>
                  </div>
                  <div class="require-item" :class="{ met: playerLevel >= breachInfo.realmLevel }">
                    <span>角色等级 ≥ {{ breachInfo.realmLevel }}</span>
                    <span class="progress">({{ playerLevel }}/{{ breachInfo.realmLevel }})</span>
                  </div>
                  <div class="require-item" v-if="breachInfo.materOneName">
                    <span>突破材料: {{ breachInfo.materOneName }}×{{ breachInfo.materOneNum }}</span>
                    <span v-if="breachInfo.materTwoName">, {{ breachInfo.materTwoName }}×{{ breachInfo.materTwoNum }}</span>
                  </div>
                  <div class="require-item" v-if="breachInfo.coin">
                    <span>需要 {{ breachInfo.coninType == 0 ? '金币' : '灵石' }} {{ breachInfo.coin }}</span>
                  </div>
                  <div class="require-item">
                    <span>基础概率: {{ (breachInfo.realmPro * 100).toFixed(2) }}%</span>
                  </div>
                  <div class="require-item" v-if="activeBreakthroughPills.length">
                    <span>突破丹加成记录</span>
                    <span class="progress">共 +{{ formatRate(pillBonusRate) }} / 上限35.00%</span>
                  </div>
                  <div
                    v-for="(pill, index) in activeBreakthroughPills"
                    :key="'breach-pill-' + index"
                    class="require-item breach-pill-record"
                  >
                    <span>{{ pill.itemName || '突破丹' }}：+{{ formatRate(pill.successRateBonus) }}</span>
                    <span class="progress">剩余 {{ formatRemainTime(getRemainingSeconds(pill)) }}</span>
                  </div>
                  <div class="require-item" v-if="otherBonusRate > 0">
                    <span>其他突破加成</span>
                    <span class="progress">+{{ formatRate(otherBonusRate) }}</span>
                  </div>
                  <div class="require-item highlight">
                    <span>当前总概率: {{ formatRate(totalBreakthroughRate) }}</span>
                  </div>
                </div>
              </div>
              <Button
                type="success"
                size="large"
                long
                :loading="breaching"
                @click="handleBreakthrough"
                style="margin-top: 16px"
              >
                突破境界
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CultivationRoom',
  props: {
    level: { type: Number, default: 1 },
    points: { type: Number, default: 0 },
    baseOutput: { type: Number, default: 100 },
    perPointBonus: { type: Number, default: 0.1 },
    spiritValue: { type: Number, default: 0 },
    playerLevel: { type: Number, default: 1 },
    playerExp: { type: Number, default: 0 },
    playerMaxExp: { type: Number, default: 0 },
    breachInfo: { type: Object, default: null }
  },
  data() {
    return {
      collectAmount: 0,
      collecting: false,
      breaching: false,
      nowTick: Date.now(),
      pillTimer: null
    }
  },
  computed: {
    outputPerHour() {
      return Math.floor(this.baseOutput * (1 + this.points * this.perPointBonus))
    },
    displayExp() {
      return Math.floor(this.playerExp || 0)
    },
    displayMaxExp() {
      return this.playerMaxExp || 0
    },
    activeBreakthroughPills() {
      const list = this.breachInfo && Array.isArray(this.breachInfo.breakthroughPillEffects)
        ? this.breachInfo.breakthroughPillEffects
        : []
      return list.filter(item => this.getRemainingSeconds(item) > 0)
    },
    pillBonusRate() {
      return Math.min(0.35, this.activeBreakthroughPills.reduce((sum, item) => sum + Number(item.successRateBonus || 0), 0))
    },
    otherBonusRate() {
      return Number((this.breachInfo && this.breachInfo.realmOtherPro) || 0)
    },
    totalBreakthroughRate() {
      const base = Number((this.breachInfo && this.breachInfo.realmPro) || 0)
      const max = Number((this.breachInfo && this.breachInfo.realmMaxPro) || 1)
      return Math.min(max, base + this.pillBonusRate + this.otherBonusRate)
    }
  },
  mounted() {
    this.pillTimer = setInterval(() => {
      this.nowTick = Date.now()
      if (this.activeBreakthroughPills.length === 0 && this.breachInfo && Number(this.breachInfo.realmDanYaoPro || 0) > 0) {
        this.$emit('breached')
      }
    }, 30000)
  },
  beforeDestroy() {
    if (this.pillTimer) clearInterval(this.pillTimer)
  },
  watch: {
    spiritValue: {
      handler(val) {
        this.updateCollectDefault()
      },
      immediate: true
    },
    playerExp() {
      this.updateCollectDefault()
    },
    playerMaxExp() {
      this.updateCollectDefault()
    }
  },
  methods: {
    formatRate(value) {
      return `${(Number(value || 0) * 100).toFixed(2)}%`
    },
    getRemainingSeconds(pill) {
      if (!pill) return 0
      const endAt = Number(pill.endAt || 0)
      return endAt > 0
        ? Math.max(0, Math.ceil((endAt - this.nowTick) / 1000))
        : Math.max(0, Number(pill.remainingSeconds || 0))
    },
    formatRemainTime(seconds) {
      const remain = Math.max(0, Number(seconds || 0))
      const hours = Math.floor(remain / 3600)
      const minutes = Math.floor((remain % 3600) / 60)
      if (hours > 0) return `${hours}小时${minutes}分`
      return `${minutes}分${Math.floor(remain % 60)}秒`
    },
    updateCollectDefault() {
      const spirit = Math.floor(this.spiritValue || 0)
      const expDiff = Math.max(0, this.displayMaxExp - this.displayExp)

      if (spirit === 0) {
        this.collectAmount = 0
      } else if (expDiff <= 0) {
        this.collectAmount = spirit
      } else if (spirit >= expDiff) {
        this.collectAmount = expDiff
      } else {
        this.collectAmount = spirit
      }
    },
    async handleCollect() {
      const max = Math.floor(this.spiritValue || 0)
      if (this.collectAmount <= 0 || this.collectAmount > max) {
        this.$Message.warning(`请输入1-${max}之间的数量`)
        return
      }

      this.collecting = true
      try {
        const res = await this.$http.post(
          `/gameCave/collectSpirit/?charaId=${this.getCookie('charaId')}&amount=${this.collectAmount}`
        )
        if (res.data.status === 200) {
          this.$Message.success(res.data.data || '收取成功')
          this.$emit('collected')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '收取失败')
      } finally {
        this.collecting = false
      }
    },
    async handleBreakthrough() {
      this.breaching = true
      try {
        const res = await this.$http.post(
          `/gameRealm/breakThroughTheRealm?charaId=${this.getCookie('charaId')}`
        )
        if (res.data.status === 200) {
          this.$Message.success(res.data.msg || '突破成功！')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '突破失败')
      } finally {
        this.$emit('breached')
        this.breaching = false
      }
    },
    getCookie(name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) return parts.pop().split(';').shift()
      return ''
    }
  }
}
</script>

<style scoped>
.cultivation-room {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.room-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #19be6b 0%, #47c9af 100%);
  border-radius: 12px 12px 0 0;
  color: #fff;
  flex-shrink: 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.room-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.room-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.room-desc {
  margin: 4px 0 0;
  font-size: 13px;
  opacity: 0.9;
}

.header-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-label {
  font-size: 11px;
  opacity: 0.8;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
}

.room-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.spirit-card,
.breach-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
}

.spirit-display {
  padding: 20px;
  text-align: center;
}

.spirit-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 3px solid #ff9900;
  margin: 0 auto 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle, rgba(255, 153, 0, 0.1) 0%, transparent 70%);
}

.spirit-amount {
  font-size: 32px;
  font-weight: 700;
  color: #ff9900;
}

.spirit-unit {
  font-size: 12px;
  color: #808695;
}

.spirit-rate {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: #808695;
  margin-bottom: 16px;
}

.collect-section {
  padding: 0 20px 20px;
}

.breach-content {
  padding: 16px;
}

.breach-requirements {
  margin-bottom: 16px;
}

.require-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d8cf0;
  margin-bottom: 12px;
}

.require-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.require-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 13px;
  color: #515a6e;
}

.require-item.met {
  color: #19be6b;
  background: #f6ffed;
}

.breach-pill-record {
  border-left: 3px solid #2d8cf0;
  background: #f0f8ff;
}

.breach-pill-record .progress {
  margin-left: 12px;
  white-space: nowrap;
}

.require-item.highlight {
  color: #2d8cf0;
  font-weight: 600;
  background: #e6f7ff;
}

.progress {
  font-size: 12px;
  color: #808695;
}

@media (max-width: 768px) {
  .room-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-stats {
    width: 100%;
    justify-content: space-around;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/cave/CultivationRoom.vue